import { Box, CardActions, CardContent, Typography } from "@mui/material";
import React from "react";
import ArrowRight from "../../../../assets/arrow_right.svg";
import { useNavigate } from "react-router-dom";
import { FamilyEnquiryLabels } from "../../family_enquiries/family_enquiry_list";
import { EnquiredSchemes } from "../../models/family_enquiry_list";
import dayjs from "dayjs";
import { useFamilyEnquiriesFilterStore } from "../../../../store/use-scheme-family-enquiries-filter-store";
import { green, orange, purple } from "../../../../theme/colors";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";

const ROWS_PER_PAGE = 8;

interface DataGridInterface {
  labels: FamilyEnquiryLabels[];
  data: EnquiredSchemes[];
  count: number;
  page: number;
  setPage: (val: number) => void;
}

export default function DataGridFamilyEnquiries({
  labels,
  data,
  count,
  page,
  setPage,
}: DataGridInterface) {
  const navigate = useNavigate();
  const { setApplyFilter } = useFamilyEnquiriesFilterStore();

  const startRange = (page - 1) * ROWS_PER_PAGE + 1;
  const endRange = Math.min(page * ROWS_PER_PAGE, count);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setApplyFilter(true);
    setPage(newPage + 1);
  };

  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = !!data && data.length > 0;

  function notificationStatusColor(status?: string) {
    if (status === "Success") {
      return green;
    } else if (status === "Failure") {
      return orange;
    } else {
      return purple;
    }
  }

  return (
    <Box display={"flex"} sx={{ height: "100%", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        }}
      >
        <CardContent
          sx={{ display: "flex", flexDirection: "column", flexGrow: 1, p: { xs: 2.5, sm: 3 } }}
        >
          {!showList ? (
            <TableEmptyState
              message="No family enquiries found"
              hint="Try adjusting your search or filters."
            />
          ) : (
            <Box mt={1}>
              <TableHeaderRow labels={labels} lastColumnWidth={170} align="left" bordered />
              {data?.map((item) => {
                const originalDate = dayjs(item?.enquiredOn);
                const formattedDate = originalDate?.format?.("DD MMM YYYY");
                return (
                  <TableRow
                    key={item?.enquiryId}
                    onClick={() =>
                      navigate(`/schemes/familyEnquiries/${item?.enquiryId}`, {
                        state: item,
                      })
                    }
                    bordered
                  >
                    <TableCell label="Ref.No" bordered>
                      <Typography fontSize={14} noWrap>
                        {item?.enquiryId}
                      </Typography>
                    </TableCell>
                    <TableCell label="Primary Username" bordered>
                      <Typography fontSize={14} noWrap>
                        {item.name}
                      </Typography>
                    </TableCell>
                    <TableCell label="Primary User Contact Number" bordered>
                      <Typography fontSize={14} noWrap>
                        {item.mobileNumber}
                      </Typography>
                    </TableCell>
                    <TableCell label="Scheme Enquired" bordered>
                      <Typography fontSize={14} noWrap>
                        {item.schemeName}
                      </Typography>
                    </TableCell>
                    <TableCell label="Family Member Name" bordered>
                      <Typography fontSize={14} noWrap>
                        {item?.memberName}
                      </Typography>
                    </TableCell>
                    <TableCell label="Relationship with Primary User" bordered>
                      <Typography fontSize={14} noWrap>
                        {item.relationship}
                      </Typography>
                    </TableCell>
                    <TableCell label="Enquired On" bordered>
                      <Typography fontSize={14} noWrap>
                        {formattedDate}
                      </Typography>
                    </TableCell>
                    <TableCell label="Notification Status" isLast lastColumnWidth={170} bordered>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          fontSize={14}
                          fontWeight={600}
                          color={notificationStatusColor(item.notificationStatus)}
                          noWrap
                        >
                          {item.notificationStatus || "-"}
                        </Typography>
                        <Box sx={{ display: { xs: "none", sm: "block" } }}>
                          <img src={ArrowRight} alt="" height={14} width={14} />
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </Box>
          )}
        </CardContent>
        {showList && (
          <CardActions sx={{ px: 4, pb: 3, mt: "auto" }}>
            <Box width="100%">
              <TableFooterPagination
                count={count}
                page={page - 1}
                onPageChange={handleChangePage}
                summaryText={customText}
                rowsPerPage={ROWS_PER_PAGE}
              />
            </Box>
          </CardActions>
        )}
      </Box>
    </Box>
  );
}
